import { z } from 'zod'
import { MAX_CHAT_MESSAGE, MARKER_COLORS } from './constants'
import { SESSION_ROLES } from './roles'

/**
 * Recording state of a studio. The API is the only writer: it stores it in Postgres and mirrors it
 * into the LiveKit room metadata, so late joiners and reconnecting clients read the same truth.
 */
export const recordingStateSchema = z.object({
  recordingId: z.string(),
  status: z.enum(['recording', 'paused']),
  takeId: z.string().nullable(),
  takeIndex: z.number().int(),
  /** Server epoch ms. Lies in the future during the countdown. */
  takeStartsAt: z.number().nullable(),
  /** Sum of all finished takes, so every client shows the same timer. */
  elapsedBeforeTakeMs: z.number(),
})
export type RecordingState = z.infer<typeof recordingStateSchema>

export const roomStateSchema = z.object({
  v: z.literal(1),
  recording: recordingStateSchema.nullable(),
})
export type RoomState = z.infer<typeof roomStateSchema>

export const EMPTY_ROOM_STATE: RoomState = { v: 1, recording: null }

export function parseRoomState(raw: string | undefined | null): RoomState {
  if (!raw) return EMPTY_ROOM_STATE
  try {
    const parsed = roomStateSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : EMPTY_ROOM_STATE
  } catch {
    return EMPTY_ROOM_STATE
  }
}

/** Set by the API in the LiveKit token; participants cannot change it (canUpdateOwnMetadata is off). */
export const participantMetaSchema = z.object({
  role: z.enum(SESSION_ROLES),
  kind: z.enum(['member', 'guest']),
})
export type ParticipantMeta = z.infer<typeof participantMetaSchema>

export function parseParticipantMeta(raw: string | undefined | null): ParticipantMeta {
  try {
    const parsed = participantMetaSchema.safeParse(JSON.parse(raw ?? ''))
    if (parsed.success) return parsed.data
  } catch {
    // fall through
  }
  return { role: 'guest', kind: 'guest' }
}

/** Broadcast by every client every few seconds; sender identity is verified by the SFU. */
export const statusMessageSchema = z.object({
  recording: z.enum(['idle', 'recording', 'error']),
  /** Bytes recorded locally but not yet on the server. */
  backlogBytes: z.number().nonnegative(),
  uploadedBytes: z.number().nonnegative(),
  uploading: z.boolean(),
  storageLow: z.boolean(),
  error: z.string().max(200).optional(),
})
export type StatusMessage = z.infer<typeof statusMessageSchema>

export const chatMessageSchema = z.object({
  id: z.string().max(64),
  text: z.string().min(1).max(MAX_CHAT_MESSAGE),
  at: z.number(),
})
export type ChatMessage = z.infer<typeof chatMessageSchema>

export const markerBroadcastSchema = z.object({
  id: z.string(),
  at: z.number(),
  offsetMs: z.number(),
  label: z.string(),
  color: z.enum(MARKER_COLORS),
  authorName: z.string(),
})
export type MarkerBroadcast = z.infer<typeof markerBroadcastSchema>

export const noticeSchema = z.object({
  kind: z.enum(['muted-by-producer', 'promoted', 'demoted']),
  by: z.string(),
})
export type Notice = z.infer<typeof noticeSchema>

const encoder = new TextEncoder()
const decoder = new TextDecoder()

export function encodeData(value: unknown): Uint8Array<ArrayBuffer> {
  return encoder.encode(JSON.stringify(value))
}

export function decodeData<T>(schema: z.ZodType<T>, payload: Uint8Array): T | null {
  try {
    const parsed = schema.safeParse(JSON.parse(decoder.decode(payload)))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

const ROOM_PREFIX = 'studio_'

export function livekitRoomName(studioId: string): string {
  return `${ROOM_PREFIX}${studioId}`
}

export function studioIdFromRoomName(room: string): string | null {
  return room.startsWith(ROOM_PREFIX) ? room.slice(ROOM_PREFIX.length) : null
}
